import React from 'react';
import {
  Label,
  Input,
  TextArea,
  SecondaryButton,
  CheckButton,
  IconButton,
  Title,
} from 'components';

import Plus from 'assets/icons/plus.svg';

import {Choice} from '../../types';
import i18n from 'i18next';

type MultipleChoiceFormProps = {
  title?: string;
  setTitle: (value: string) => void;
  choices?: Choice[];
  setChoices: (value: Choice[]) => void;
  feedback?: string;
  setFeedback: (value: string) => void;
  disabled?: boolean;
  isEdit?: boolean;
};

function MultipleChoiceForm({
  title,
  setTitle,
  choices,
  setChoices,
  feedback,
  setFeedback,
  disabled,
  isEdit,
}: MultipleChoiceFormProps) {
  const onAddChoice = () => {
    setChoices([
      ...(choices || []),
      {key: choices?.length || 0, text: '', isAnswer: false},
    ]);
  };

  const onRemoveChoice = (index: number) => () => {
    if (choices) {
      setChoices(choices.filter((_choice, cIndex) => cIndex !== index));
    }
  };

  const onUpdateChoices =
    (index: number) => (event: React.ChangeEvent<HTMLInputElement>) => {
      if (choices) {
        setChoices(
          choices.map((choice, cIndex) =>
            cIndex === index ? {...choice, text: event.target.value} : choice
          )
        );
      }
    };

  const onSelectAnswer = (index: number, isAnswer?: boolean) => () => {
    if (choices) {
      const newChoices = choices.map((choice, cIndex) => {
        if (cIndex === index) {
          return {...choice, isAnswer};
        } else if (isAnswer) {
          return {...choice, isAnswer: false};
        }

        return choice;
      });

      setChoices(newChoices);
    }
  };

  return (
    <>
      <Title.Sub className="mb-10">
        {i18n.t('wisbitPage.addComponentModal.multipleChoiceForm.title')}
      </Title.Sub>
      <Label
        htmlFor="multiple-title"
        label={i18n.t('wisbitPage.addComponentModal.multipleChoiceForm.ask')}
      >
        {!isEdit && !disabled && (
          <Input
            id="multiple-title"
            value={title}
            onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
              setTitle(event.target.value)
            }
            className="mb-6"
            disabled={disabled}
          />
        )}
        {disabled && (
          <div className="mb-4">
            {i18n.t(
              'wisbitPage.addComponentModal.multipleChoiceForm.askInTextPart'
            )}
          </div>
        )}
        {isEdit && (
          <div className="mb-4">
            {i18n.t(
              'wisbitPage.addComponentModal.multipleChoiceForm.editInTextPart'
            )}
          </div>
        )}
      </Label>
      {choices &&
        choices.map(({key, text, isAnswer}, index) => (
          <Label
            htmlFor={`choices_${index}`}
            label={i18n.t(
              'wisbitPage.addComponentModal.multipleChoiceForm.optionCount',
              {count: index + 1}
            )}
            key={key}
          >
            <div className="flex items-center justify-between mb-4">
              <Input
                id={`choices_${index}`}
                value={text}
                onChange={onUpdateChoices(index)}
                className="p-4"
                disabled={disabled}
              >
                {choices.length > 1 && (
                  <CheckButton
                    checked={isAnswer}
                    onClick={onSelectAnswer(index, !isAnswer)}
                    disabled={disabled}
                  />
                )}
              </Input>
              {!disabled && choices.length > 1 && (
                <IconButton type="delete" onClick={onRemoveChoice(index)} />
              )}
            </div>
          </Label>
        ))}
      {!disabled && (
        <SecondaryButton
          onClick={onAddChoice}
          className="h-12 w-20 flex items-center justify-center my-4"
        >
          {i18n.t('wisbitPage.addComponentModal.multipleChoiceForm.option')}
          <img src={Plus} alt="plus" />
        </SecondaryButton>
      )}
      <Label
        htmlFor="multiple-feedback"
        label={i18n.t(
          'wisbitPage.addComponentModal.multipleChoiceForm.explanation'
        )}
        className="mt-4"
      >
        <TextArea
          id="multiple-feedback"
          value={feedback}
          onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
            setFeedback(event.target.value)
          }
          disabled={disabled}
        />
      </Label>
    </>
  );
}

export default MultipleChoiceForm;
